import React from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import {
  faPrint,
  faRightLeft,
  faRotateLeft,
} from '@fortawesome/free-solid-svg-icons'
import ProfileSection from 'app/common/pixel-menu/_comp/ProfileSection'
import ArrowRightFromBracketIcon from 'client/utils/icons/arrow-right-from-bracket-icon'
import {
  DcBreadcrumbs,
  DcButton,
  DcFlexBox,
  DcInput,
  DcOverlaySlider,
  PixelFactoryContext,
  PixelFlexBox,
  PixelIcon,
  PixelPageContainer,
  PixelTable,
  PixelTableAction,
  Section,
  SectionActions,
  SectionContent,
  colors,
} from 'pixel-base'
import { AjaxResponse } from 'rxjs/ajax'
import styled from 'styled-components'
import {
  get,
  getPrintLink,
  getReturnBatchDetails,
  post,
} from 'utils/api-routes.util'
import * as XLSX from 'xlsx'

import ResendOrderPopup from './components/ResendOrderPopup'
import { IPixelFactory } from '../../../../../../decl.d'
import OrdersCreateBulkConsignments from '../../../components/OrdersCreateBulkConsignments'

const breadcrumbs = [
  {
    name: 'Orders',
    active: false,
    link: '/orders',
  },
  {
    name: 'Returns Dashboard',
    active: false,
    link: '/orders/returns',
  },
  {
    name: 'Received Returns Batches',
    active: false,
    link: '/orders/returns/received',
  },
  {
    name: 'Details',
    active: true,
  },
]
const columns = [
  {
    dataKey: 'source_order_id',
    name: 'Ref #',
    sortable: true,
    selector: (row) => row.source_order_id,
  },
  {
    dataKey: 'tracking_id',
    name: 'CN #',
    sortable: true,
    selector: (row) => row.tracking_id,
  },
  {
    dataKey: 'customer',
    name: 'Consignee',
    sortable: true,
    selector: (row) => row.customer,
  },
  {
    dataKey: 'city',
    name: 'City',
    sortable: true,
    selector: (row) => row.city,
  },
  {
    dataKey: 'item_qty',
    name: 'Item Quantity',
    sortable: true,
    selector: (row) => row.item_qty,
  },
]

const ReceivedDetail = () => {
  const params = useParams()

  const [searchParams] = useSearchParams()
  const { showPageLoader, hidePageLoader, showSuccess, showFailure } =
    React.useContext<IPixelFactory>(PixelFactoryContext)
  const [data, setData] = React.useState([])
  const [tableLoading, setTableLoading] = React.useState(false)
  const [tableColumns, setTableColumns] = React.useState(columns)
  const [filters, setFilters] = React.useState<IFilters>({
    page: 0,
    search: '',
    batch_id: 0,
    courier_id: 0,
  })
  const [dispatches, setDispatches] = React.useState([])
  const [showModal, setShowModal] = React.useState(false)
  const [showConsignmentModal, setShowConsignmentModal] = React.useState(false)
  const [selectedDispatch, setSelectedDispatch] = React.useState<any>({})
  const [resetPaginationToggle, setResetPaginationToggle] =
    React.useState(false)
  const action = [
    {
      title: 'Resend Order to Same Courier',
      icon: <PixelIcon fontSize="18px" icon={faRotateLeft} />,
      clickHandler: (row) => {
        setSelectedDispatch(row)
        setShowModal(true)
      },
    },
  ]
  const actions = [
    {
      title: 'Resend Order to Different Courier',
      icon: <PixelIcon fontSize="18px" icon={faRightLeft} />,
      clickHandler: (row) => {
        setSelectedDispatch(row)
        setShowConsignmentModal(true)
      },
    },
  ]
  React.useEffect(() => {
    const params: any = searchParams
    if (params.dispatch) setFilters(params)
    setTableColumns([
      ...columns,
      {
        dataKey: 'actions',
        name: 'Actions',
        sortable: true,
        style: {
          'div:first-child': {
            overflow: 'visible !important',
          },
        },
        selector: (row) => (
          <PixelFlexBox gap="5px">
            <PixelTableAction row={row} action={action} />
            <PixelTableAction row={row} action={actions} />
          </PixelFlexBox>
        ),
      },
    ])
  }, [])

  const getDispatchList = () => {
    get(`/courier/${filters.courier_id}/dispatch`).subscribe((res: any) => {
      setDispatches(res.response.data)
    })
  }

  const getOrders = () => {
    setTableLoading(true)
    getReturnBatchDetails(filters.batch_id).subscribe((res: any) => {
      setData(res.response.data)
      setTableLoading(false)
    })
  }
  const subHeaderComponentMemo = React.useMemo(() => {
    return (
      <PixelFlexBox justifyContent="flex-end">
        <FilterInput>
          <DcInput
            placeholder="Search"
            showsearchicon={0}
            inputSize="small"
            name="Search"
            value={filters.search}
            onChange={(e) => {
              setResetPaginationToggle(e.target.value.length > 0)
              setFilters({
                ...filters,
                search: e.target.value,
              })
            }}
          />
        </FilterInput>
      </PixelFlexBox>
    )
  }, [filters, resetPaginationToggle])

  const handlePageChange = (page) => {
    setFilters({
      ...filters,
      page: page - 1,
    })
  }
  React.useEffect(() => {
    filters.batch_id = params?.batch_id
    filters.courier_id = params?.courier_id
  }, [params])

  React.useEffect(() => {
    getOrders()
    getDispatchList()
  }, [filters])

  return (
    <>
      <DcFlexBox
        height="60px"
        fontSize="16px"
        style={{
          fontSize: '16px',
          padding: '8px 20px',
          borderBottom: `1px solid ${colors.grayBorderLight}`,
        }}
      >
        <DcFlexBox
          justifyContent="space-between"
          alignItems="center"
          padding=".5rem 0px"
        >
          <DcBreadcrumbs fontSize="14px" data={breadcrumbs} />
          <ProfileSection />
        </DcFlexBox>
      </DcFlexBox>

      <PixelPageContainer style={{ padding: '0px' }}>
        <Section style={{ padding: '0px' }}>
          <SectionContent style={{ padding: '0px', borderRadius: '0px' }}>
            <SectionActions
              style={{
                width: '100%',
                display: 'flex',
                justifyContent: 'flex-end',
                padding: '5px 20px 5px 10px',
                background: `${colors.white}`,
              }}
            >
              <DcButton
                size="sm"
                variant="primary"
                onClick={() =>
                  window.open(
                    getPrintLink('returns/restock', filters.batch_id),
                    '_blank',
                  )
                }
              >
                <PixelIcon color="white" icon={faPrint} /> Print Return Restock
              </DcButton>
              &nbsp;
              <DcButton
                size="sm"
                withIcon={true}
                variant="outline-secondary"
                onClick={() => {
                  const userInfo = JSON.parse(
                    localStorage.getItem('user_info') || '{}',
                  )
                  const token = userInfo?.token || ''
                  get(
                    `/export/returns/restock/${token}/${filters.batch_id}`,
                    {},
                  ).subscribe((res: any) => {
                    const payload =
                      res?.response?.data || res?.response?.items || []
                    const rows = (payload || []).map((item: any) => {
                      const sku =
                        item?.sku ||
                        item?.product_sku ||
                        item?.product?.product_sku ||
                        ''
                      const productName =
                        item?.name ||
                        item?.product_name ||
                        item?.product?.product_name ||
                        ''
                      const qty =
                        item?.quantity || item?.qty || item?.item_qty || 0
                      return {
                        'Product SKU': sku,
                        'Product Name': productName,
                        Quantity: qty,
                      }
                    })
                    if (rows.length > 0) {
                      const ws = XLSX.utils.json_to_sheet(rows, {
                        header: ['Product SKU', 'Product Name', 'Quantity'],
                      })
                      const wb = XLSX.utils.book_new()
                      XLSX.utils.book_append_sheet(wb, ws, 'Restock')
                      const fileName = `return_restock_${filters.batch_id}.xlsx`
                      XLSX.writeFile(wb, fileName)
                      showSuccess('Exported successfully')
                    } else if (res?.response?.success) {
                      showSuccess(res.response.message)
                    } else {
                      showFailure(res?.response?.error || 'No data to export')
                    }
                  })
                }}
              >
                <ArrowRightFromBracketIcon size={16} />
                <span>Export</span>
              </DcButton>
            </SectionActions>
          </SectionContent>
        </Section>
        <Section style={{ padding: '20px' }}>
          <SectionContent style={{ padding: ' 10px' }}>
            <PixelTable
              pagination={data?.length > 10}
              columns={tableColumns}
              progressPending={tableLoading}
              data={data}
              subHeaderComponent={subHeaderComponentMemo}
              paginationResetDefaultPage={resetPaginationToggle}
              //   paginationServer
              //   paginationTotalRows={totalRows}
              fixedHeader={true}
              fixedHeaderScrollHeight="calc(100vh - 230px)"
              highlightOnHover={true}
              isSearchable={true}
              pointerOnHover={true}
              paginationPerPage={50}
              onChangePage={handlePageChange}
            />
          </SectionContent>
        </Section>
      </PixelPageContainer>

      {showModal && (
        <DcOverlaySlider
          title={`Resend Order # ${selectedDispatch?.source_order_id}`}
          onClose={() => setShowModal(false)}
          isOpen={showModal}
          width="50%"
        >
          {showModal && (
            <ResendOrderPopup
              dispatches={dispatches}
              selectedDispatch={selectedDispatch}
              warehouseId={dispatches[0]?.warehouse_id}
              handleClose={() => {
                setShowModal(false)
                getOrders()
              }}
            />
          )}
        </DcOverlaySlider>
      )}
      {showConsignmentModal && (
        <DcOverlaySlider
          title={'Resend with new consignment'}
          onClose={() => setShowConsignmentModal(false)}
          isOpen={showConsignmentModal}
          width="50%"
        >
          {showConsignmentModal && (
            <OrdersCreateBulkConsignments
              onCreateConsignment={(data) => {
                const payloadData = {
                  ...data,
                  return_order_id: selectedDispatch?.id,
                  return_batch_id: selectedDispatch?.return_batch_id,
                }
                showPageLoader()
                post(
                  `/orders/${selectedDispatch.number}/consignments/returns`,
                  payloadData,
                ).subscribe((res: AjaxResponse<any>) => {
                  hidePageLoader()
                  setShowConsignmentModal(false)
                  if (res.response.data) {
                    showSuccess(
                      data.is_dispatch
                        ? `Consignment created successfully with Dispatch ID ${res.response?.dispatch_id} `
                        : 'Consignment created successfully',
                    )
                    getOrders()
                  } else if (res.response?.error) {
                    showFailure(
                      res.response?.error?.error || res.response?.error,
                    )
                  } else {
                    showFailure(
                      res.response?.consignments?.error ||
                        'Something went wrong',
                    )
                  }
                })
              }}
              selectedOrders={[selectedDispatch]}
              isMustDispatch={true}
            />
          )}
        </DcOverlaySlider>
      )}
    </>
  )
}

const FilterInput = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  align-content: flex-start;
  gap: 10px;
`

export default ReceivedDetail
